import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { searchAll, fetchPincodeInfo } from "@/lib/api";
import { RequestServiceButton } from "@/components/RequestServiceButton";
import { ProductCard, ServiceCard, ShopCard, HireCard } from "@/components/Cards";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RadiusFilter from "@/components/RadiusFilter";
import { Search } from "lucide-react";

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const pincode = params.get("pincode") || "";
  const { radiusKm, pincode: activePin, setPincode } = useApp();
  const [data, setData] = useState({ products: [], services: [], hiring: [], shops: [], suggestions: [] });
  const [loading, setLoading] = useState(true);
  const [pinError, setPinError] = useState("");

  // A ?pincode= from the URL is used only if the backend confirms it is serviceable.
  useEffect(() => {
    // No PIN in the link: use the browsing PIN currently selected in the Header (never the profile PIN directly).
    if (!pincode && activePin) {
      const next = new URLSearchParams(params);
      next.set("pincode", activePin);
      setParams(next, { replace: true });
      return undefined;
    }
    let alive = true;
    setLoading(true); setPinError("");
    const check = !pincode ? Promise.resolve()
      : !/^\d{6}$/.test(pincode) ? Promise.reject(new Error(`"${pincode}" is not a valid 6-digit PIN code.`))
      : fetchPincodeInfo(pincode)
          .then((info) => { if (alive && pincode !== activePin) setPincode(pincode, info?.area || null); }) // sync header PIN from a shared link
          .catch((e) => { throw new Error(e?.response?.status === 404 ? `PIN ${pincode} verify nahi ho paya. Sahi PIN ya area chunein.` : "Could not verify this PIN. Please check your connection and try again."); });
    check
      .then(() => searchAll({ q, pincode, radius_km: radiusKm ?? undefined, limit: 20 }).then((d) => alive && setData(d)))
      .catch((e) => { if (alive) { setPinError(e.message); setData({ products: [], services: [], hiring: [], shops: [] }); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [q, pincode, radiusKm]);

  // Header PIN changed while on this page -> same search for the new PIN (replace: no extra Back entry).
  const prevPin = useRef(activePin);
  useEffect(() => {
    if (prevPin.current === activePin) return;
    prevPin.current = activePin;
    if ((activePin || "") === pincode) return;
    const next = new URLSearchParams(params);
    if (activePin) next.set("pincode", activePin); else next.delete("pincode");
    setParams(next, { replace: true });
  }, [activePin]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = data.products.length + data.services.length + data.hiring.length + data.shops.length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="search-results-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-700 mb-2">Search results</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
          {q ? <>Results for "<span className="text-emerald-700">{q}</span>"</> : "All items near you"}
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          {loading ? "Searching..." : pinError ? "PIN verify nahi hua" : `${total} ${total === 1 ? "result" : "results"}${pincode ? ` near pincode ${pincode}` : ""}`}{pincode && !loading && !pinError ? " · Same-pincode results shown first" : ""}
        </div>
      </div>
      <div className="mb-6"><RadiusFilter /></div>

      {!loading && pinError && (
        <div className="text-center py-24" data-testid="search-pin-error">
          <div className="font-display text-xl font-bold">{pinError}</div>
          <p className="text-sm text-slate-500 mt-1">Choose a serviceable area from the PIN selector at the top.</p>
          <Link to="/" className="inline-block mt-4 text-sm font-semibold text-emerald-700 underline">← Back to home</Link>
        </div>
      )}

      {!loading && !pinError && total === 0 && (
        <div className="text-center py-24">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Search className="w-7 h-7 text-slate-400" />
          </div>
          <div className="font-display text-xl font-bold" data-testid="search-no-results">Kuch nahi mila</div>
          <p className="text-sm text-slate-500 mt-1">Try a different keyword or change your pincode.</p>
          {data.suggestions?.length > 0 && (
            <div className="mt-5 flex flex-col items-center gap-2" data-testid="search-suggestions">
              {data.suggestions.slice(0, 3).map((s) => (
                <Link key={s} to={`/search?q=${encodeURIComponent(s)}${pincode ? `&pincode=${pincode}` : ""}`} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 px-4 py-2 text-sm min-h-[40px]" data-testid={`search-suggestion-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  Kya aap <b className="font-semibold">{s}</b> dhoondh rahe hain?
                </Link>
              ))}
            </div>
          )}
          {q.trim().length >= 2 && <RequestServiceButton term={q.trim()} pincode={pincode || activePin} />}
          <Link to="/" className="inline-block mt-4 text-sm font-semibold text-emerald-700 underline">← Back to home</Link>
        </div>
      )}

      {total > 0 && (
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="bg-white border border-slate-200 rounded-full p-1 mb-6 h-auto inline-flex max-w-full overflow-x-auto overscroll-x-contain justify-start [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>button]:flex-shrink-0" data-testid="search-tabs-list">
            <TabsTrigger value="all" className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-all">All ({total})</TabsTrigger>
            <TabsTrigger value="products" className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-products">Products ({data.products.length})</TabsTrigger>
            <TabsTrigger value="services" className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-services">Services ({data.services.length})</TabsTrigger>
            <TabsTrigger value="shops" className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-shops">Shops ({data.shops.length})</TabsTrigger>
            <TabsTrigger value="hiring" className="rounded-full px-4 py-1.5 text-sm data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-hiring">Jobs ({data.hiring.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-10">
            {data.products.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold mb-4">Products</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
                  {data.products.slice(0, 5).map((p) => <ProductCard key={p.id} product={p} />)}
                </div>
              </div>
            )}
            {data.shops.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold mb-4">Shops</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.shops.slice(0, 3).map((s) => <ShopCard key={s.id} shop={s} />)}
                </div>
              </div>
            )}
            {data.services.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold mb-4">Services</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
                  {data.services.slice(0, 4).map((s) => <ServiceCard key={s.id} service={s} />)}
                </div>
              </div>
            )}
            {data.hiring.length > 0 && (
              <div>
                <h2 className="font-display text-xl font-bold mb-4">Jobs</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.hiring.slice(0, 3).map((h) => <HireCard key={h.id} job={h} />)}
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="products">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
              {data.products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </TabsContent>
          <TabsContent value="services">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
              {data.services.map((s) => <ServiceCard key={s.id} service={s} />)}
            </div>
          </TabsContent>
          <TabsContent value="shops">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.shops.map((s) => <ShopCard key={s.id} shop={s} />)}
            </div>
          </TabsContent>
          <TabsContent value="hiring">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.hiring.map((h) => <HireCard key={h.id} job={h} />)}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
